"use client";

import { Layer, Source } from "react-map-gl";
import { MRT_LINES, type MrtLineCode } from "@/lib/v2/mrt";

/**
 * MRT and LRT lines (and optionally stations) for any react-map-gl map.
 * Data lives in `public/map/mrt-lines.json` and `public/map/mrt-stations.json`,
 * traced from OpenStreetMap route relations (ODbL). Each feature carries its
 * official line colour, so the layers read it straight from the data.
 * Rebuild recipe: docs/REDESIGN.md, fix round 11.
 */
export function MrtLayers({ stations = true }: { stations?: boolean }) {
  const round = { "line-cap": "round" as const, "line-join": "round" as const };
  return (
    <>
      <Source id="mrt-lines" type="geojson" data="/map/mrt-lines.json">
        <Layer id="mrt-lines-casing" type="line" layout={round} paint={{ "line-color": "#ffffff", "line-width": ["interpolate", ["linear"], ["zoom"], 10, 3, 14, 6], "line-opacity": 0.9 }} />
        <Layer id="mrt-lines-open" type="line" filter={["==", ["get", "status"], "open"]} layout={round} paint={{ "line-color": ["get", "colour"], "line-width": ["interpolate", ["linear"], ["zoom"], 10, 1.8, 14, 3.5] }} />
        <Layer id="mrt-lines-future" type="line" filter={["!=", ["get", "status"], "open"]} layout={round} paint={{ "line-color": ["get", "colour"], "line-width": ["interpolate", ["linear"], ["zoom"], 10, 1.8, 14, 3.5], "line-dasharray": [1.5, 1.5] }} />
      </Source>
      {stations && (
        <Source id="mrt-stations" type="geojson" data="/map/mrt-stations.json">
          <Layer
            id="mrt-stations-dot"
            type="circle"
            minzoom={11}
            paint={{
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 11, 2.5, 15, 6],
              "circle-color": ["get", "colour"],
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 1.5,
            }}
          />
          <Layer
            id="mrt-stations-label"
            type="symbol"
            minzoom={13}
            layout={{
              "text-field": ["get", "name"],
              "text-size": 11,
              "text-anchor": "top",
              "text-offset": [0, 0.8],
              "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Regular"],
            }}
            paint={{ "text-color": "#111827", "text-halo-color": "#ffffff", "text-halo-width": 1.2 }}
          />
        </Source>
      )}
    </>
  );
}

const LEGEND: MrtLineCode[] = ["NS", "EW", "NE", "CC", "DT", "TE"];

/** Small colour key for the lines. Sits over the map, bottom left. */
export function MrtLegend({ className }: { className?: string }) {
  return (
    <ul className={className} aria-label="MRT line colours">
      {LEGEND.map((code) => (
        <li key={code} className="flex items-center gap-1 text-[10px] font-semibold text-ink-muted">
          <span className="size-2 rounded-full" style={{ backgroundColor: MRT_LINES[code].colour }} aria-hidden="true" />
          {code}
        </li>
      ))}
      <li className="flex items-center gap-1 text-[10px] font-semibold text-ink-muted">
        <span className="size-2 rounded-full" style={{ backgroundColor: MRT_LINES.BP.colour }} aria-hidden="true" />
        LRT
      </li>
    </ul>
  );
}
